import { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { api } from '../api/client.js'
import { useApi } from '../api/useApi.js'
import { lazyChunk } from '../lib/lazyChunk.js'
import LiveBadge from '../components/LiveBadge.jsx'
import ProgressBar from '../components/ProgressBar.jsx'
import { pickI18n } from '../i18n'
import { ErrorState, Loading } from './states.jsx'

// Leaflet is ~150 kB: load the map only on this page.
const FeederMap = lazyChunk(() => import('../components/FeederMap.jsx'))

function FeederCard({ feeder, highlighted }) {
  const { t, i18n } = useTranslation()
  return (
    <Link
      to={`/feeders/${feeder.slug}`}
      className={`flex flex-col overflow-hidden rounded-[22px] border-2 bg-surface text-ink no-underline transition hover:shadow-lg ${
        highlighted ? 'border-teal' : 'border-line'
      }`}
    >
      <div className="relative aspect-video bg-mint">
        {feeder.photo_url ? (
          <img src={feeder.photo_url} alt="" className="absolute inset-0 size-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-mint">
            <img src="/brand/urbanpurr-mark.svg" alt="" className="w-2/5 opacity-60" />
          </div>
        )}
        {feeder.is_online && (
          <span className="absolute top-3 left-3">
            <LiveBadge size="sm" />
          </span>
        )}
      </div>
      <div className="flex flex-col gap-3 p-[18px]">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xl font-extrabold">{pickI18n(feeder.name, i18n.resolvedLanguage)}</span>
          <span className={`text-sm font-extrabold ${feeder.is_online ? 'text-teal' : 'text-faint'}`}>
            {feeder.is_online ? t('common.online') : t('common.offline')}
          </span>
        </div>
        <span className="text-[15px] font-semibold text-subtle">{t('feeders.meta', { city: feeder.city ?? '' })}</span>
        {feeder.food_level_percent != null && (
          <div className="flex items-center gap-2.5">
            <ProgressBar value={feeder.food_level_percent} className="h-2 flex-1" track="bg-track" />
            <span className="text-sm font-extrabold text-muted">
              {t('feeders.foodLeft', { value: Math.round(feeder.food_level_percent) })}
            </span>
          </div>
        )}
        <span className="flex justify-center rounded-[14px] bg-teal p-3.5 font-extrabold text-on-teal">
          {t('feeders.cta')}
        </span>
      </div>
    </Link>
  )
}

export default function FeedersPage() {
  const { t } = useTranslation()
  const { data, error, loading } = useApi(() => api.feeders())

  if (loading) return <Loading />
  if (error) return <ErrorState />
  const online = data.filter((f) => f.is_online).length

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-7 px-4 pt-10 pb-[72px] sm:px-8">
      <div className="flex flex-col gap-2">
        <h1 className="text-[clamp(32px,4vw,48px)] font-black tracking-[-1.2px]">{t('feeders.title')}</h1>
        <p className="text-lg text-muted">{t('feeders.lead', { count: online || data.length })}</p>
      </div>

      <div className="flex flex-wrap-reverse items-stretch gap-5">
        <div className="flex flex-[1_1_340px] flex-col gap-3.5">
          {data.map((f, i) => (
            <FeederCard key={f.id} feeder={f} highlighted={i === 0} />
          ))}
          <div className="flex flex-col gap-2.5 rounded-[22px] border-[1.5px] border-dashed border-edge p-[22px]">
            <span className="text-lg font-extrabold">{t('feeders.suggestTitle')}</span>
            <span className="text-[15px] leading-normal text-muted">{t('feeders.suggestText')}</span>
            <div className="mt-1 flex flex-wrap gap-2.5">
              <Link to="/spots" className="rounded-full bg-ink px-[18px] py-3 font-extrabold text-on-ink no-underline">
                {t('feeders.suggestCta')}
              </Link>
              <Link
                to="/contact?topic=sponsor"
                className="rounded-full border-[1.5px] border-ink px-[18px] py-3 font-extrabold text-ink no-underline"
              >
                {t('feeders.sponsorCta')}
              </Link>
            </div>
          </div>
        </div>
        <div className="relative z-0 min-h-[420px] min-w-0 flex-[999_1_560px]">
          <Suspense fallback={<div className="size-full min-h-[420px] rounded-[28px] bg-mint" />}>
            <FeederMap feeders={data} />
          </Suspense>
        </div>
      </div>
    </main>
  )
}
